// TalentPool — every candidate at this school, on the EquaOS design system:
// filters (Input + Select), the design-system Table with StatusPill, and the
// candidate record (CandidateEvidencePanel). Same calls as before:
// GET /talent-pool, GET /talent-pool/jobs, PATCH /resumes/:id/potential,
// POST /talent-pool/:id/recommendations.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import CandidateEvidencePanel from '../components/talent/CandidateEvidencePanel';
import { stageTone, matchColor } from '../components/talent/stage';
import { Card, Table, StatusPill, Button, Input, Select, EmptyState, Icon, useShellLayout } from '../ds';
import { CV_UNREAD_LABEL } from '../utils/cvRead';

// The same candidate journey the dashboard shows, in order.
const WORKFLOW_STAGES = [
  { value: 'All',                label: 'All stages'        },
  { value: 'Applied',            label: 'Applied'           },
  { value: 'Fee paid',           label: 'Fee paid'          },
  { value: 'CV screened',        label: 'CV screened'       },
  { value: 'Shortlisted',        label: 'Shortlisted'       },
  { value: 'In AI assessment',   label: 'In AI assessment'  },
  { value: 'In F2F interviews',  label: 'In F2F interviews' },
  { value: 'Offered',            label: 'Offered'           },
  { value: 'Joined',             label: 'Joined'            },
];

// ── Candidate Potential config ────────────────────────────────────────
const POTENTIAL_OPTIONS = [
  { value: 'high_potential',   label: 'High potential'   },
  { value: 'future_fit',       label: 'Future fit'       },
  { value: 'silver_medalist',  label: 'Silver medalist'  },
  { value: 'reconsider_later', label: 'Reconsider later' },
  { value: 'not_suitable',     label: 'Not suitable'     },
];

const MIN_MATCH_OPTIONS = [
  { value: '', label: 'Any' },
  { value: '40', label: '40%+' },
  { value: '60', label: '60%+' },
  { value: '70', label: '70%+' },
  { value: '80', label: '80%+' },
];

const FEE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'waived', label: 'Waived' },
];

// How many candidates one request brings back; "Show more" fetches the next.
const PAGE_SIZE = 200;

// A native select, styled with the design-system tokens: the design-system
// Select's floating list would be clipped inside the scrolling table.
function PotentialDropdown({ resumeId, value, onChange, name }) {
  const [saving, setSaving] = useState(false);
  const handleChange = async (e) => {
    const next = e.target.value || null;
    setSaving(true);
    try {
      await api.patch(`/resumes/${resumeId}/potential`, { potential: next });
      onChange(resumeId, next);
    } catch {
      toast.error('Failed to save potential');
    } finally {
      setSaving(false);
    }
  };
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <select
        className="eq-native-select"
        value={value || ''}
        onChange={handleChange}
        disabled={saving}
        aria-label={`Potential for ${name || 'this candidate'}`}
        style={{ minWidth: 150 }}
      >
        <option value="">Set potential…</option>
        {POTENTIAL_OPTIONS.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <span aria-hidden="true" style={{ position: 'absolute', right: 10, pointerEvents: 'none', color: 'var(--text-muted)', display: 'inline-flex' }}>
        {saving ? <Icon name="spinner" size={14} spin /> : '▾'}
      </span>
    </span>
  );
}

export default function TalentPool() {
  const { mobile } = useShellLayout();
  const [candidates, setCandidates]   = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]               = useState('');
  const [stageFilter, setStageFilter]     = useState('All');
  const [minScore, setMinScore]           = useState('');
  const [potentialFilter, setPotentialFilter] = useState('all');
  const [paymentFilter, setPaymentFilter]     = useState('all');
  const [reached, setReached]             = useState('');   // cumulative-stage filter from a dashboard click
  const [awaiting, setAwaiting]           = useState(false); // "to screen" queue from a dashboard click
  const [jdFilter, setJdFilter]           = useState('all');
  const [jdList, setJdList]               = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [generatingFor, setGeneratingFor]         = useState(null);

  // One school, one pool: the company dimension is gone. Every submitted CV
  // appears here, filtered by vacancy, stage, score and potential.
  const loadJDs = useCallback(() => {
    api.get('/talent-pool/jobs')
      .then(r => setJdList(r.data?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => { loadJDs(); }, []); // eslint-disable-line

  /* Every filter is applied by the server, before the list is cut into pages,
     and the count shown is the server's total. Filtering one page in the
     browser hid most of the unpaid applicants (19 Oct 2026: the dashboard
     counted 44, the list showed 6). The current filters live in a ref, so a
     change to one filter never drops another. */
  const filtersRef = useRef({});
  filtersRef.current = { search, stageFilter, minScore, potentialFilter, paymentFilter, jdFilter, reached, awaiting };
  const [page, setPage] = useState(1);

  const load = useCallback(async (overrides = {}, { append = false, pageNo = 1 } = {}) => {
    try {
      setLoading(true);
      const f = { ...filtersRef.current, ...overrides };

      const params = new URLSearchParams();
      if (f.search)                              params.append('search',        f.search);
      if (f.stageFilter && f.stageFilter !== 'All') params.append('workflowStage', f.stageFilter);
      if (f.minScore)                            params.append('minMatchScore', f.minScore);
      if (f.reached)                             params.append('reached',       f.reached);
      if (f.awaiting)                            params.append('awaiting',      '1');
      if (f.jdFilter && f.jdFilter !== 'all')    params.append('jobId',         f.jdFilter);
      if (f.potentialFilter && f.potentialFilter !== 'all') params.append('potential', f.potentialFilter);
      if (f.paymentFilter && f.paymentFilter !== 'all')     params.append('paymentStatus', f.paymentFilter);
      params.append('page', String(pageNo));
      params.append('limit', String(PAGE_SIZE));

      const r = await api.get(`/talent-pool?${params.toString()}`);
      const fetched = r.data?.data?.candidates || [];
      setCandidates(prev => (append ? [...prev, ...fetched] : fetched));
      setTotal(Number(r.data?.data?.total) || 0);
      setPage(pageNo);
    } catch (e) {
      toast.error('Failed to load talent pool');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load — honour any filter a dashboard click passed in the URL
  // (?stage= / ?reached= / ?minScore= / ?payment= / ?awaiting=), so a click
  // lands on the filtered list rather than the whole pool.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const init = {};
    const st = q.get('stage'); if (st) { setStageFilter(st); init.stageFilter = st; }
    const ms = q.get('minScore'); if (ms) { setMinScore(ms); init.minScore = ms; }
    const pay = q.get('payment'); if (pay) { setPaymentFilter(pay); init.paymentFilter = pay; }
    const rch = q.get('reached'); if (rch) { setReached(rch); init.reached = rch; }
    const awt = q.get('awaiting'); if (awt === '1' || awt === 'true') { setAwaiting(true); init.awaiting = true; }
    load(init);
  }, []); // eslint-disable-line

  // Update potential locally after save (no full reload needed)
  const handlePotentialChange = (resumeId, newValue) => {
    setCandidates(prev => prev.map(c =>
      c._id === resumeId ? { ...c, candidatePotential: newValue } : c
    ));
  };

  const handleGenerateRecs = async (candidate) => {
    try {
      setGeneratingFor(candidate._id);
      const r = await api.post(`/talent-pool/${candidate._id}/recommendations`);
      const recs = r.data?.data || [];
      // Update locally
      setCandidates(prev => prev.map(c =>
        c._id === candidate._id ? { ...c, recommendations: recs, recommendationsGeneratedAt: new Date() } : c
      ));
      // Update selected panel if open
      if (selectedCandidate?._id === candidate._id) {
        setSelectedCandidate(prev => ({ ...prev, recommendations: recs }));
      }
      toast.success('AI recommendations generated');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to generate recommendations');
    } finally {
      setGeneratingFor(null);
    }
  };


  const columns = [
    /* Fewer, fuller columns (owner, 30 Oct 2026: "a lot of scrolling"): the
       role sits under the name, and fee and stage share one column, so the
       pool fits its card instead of sliding the names out of view. */
    { key: 'candidate', label: 'Candidate', render: (c) => (
      <div style={{ display: 'grid', gap: 2 }}>
        <button type="button" className="eq-link-btn" style={{ color: 'var(--text-1)', display: 'block', textAlign: 'left' }} onClick={() => setSelectedCandidate(c)}
          aria-label={`Open ${c.candidateName}`}>{c.candidateName}</button>
        {c.appliedJD && <span style={{ color: 'var(--text-2)', fontSize: 'var(--fs-body-sm)' }}>{c.appliedJD}</span>}
        <small style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-caption)', overflowWrap: 'anywhere' }}>{c.email}</small>
      </div>
    ) },
    { key: 'status', label: 'Fee · stage', render: (c) => (
      <div style={{ display: 'grid', gap: 6, justifyItems: 'start' }}>
        {c.paymentStatus === 'paid'
          ? <StatusPill tone="success">Paid</StatusPill>
          : c.paymentStatus === 'waived'
            ? <StatusPill tone="info">Waived</StatusPill>
            : <StatusPill tone="neutral">Unpaid</StatusPill>}
        <StatusPill tone={stageTone(c.workflowStage)}>{c.workflowStage}</StatusPill>
      </div>
    ) },
    { key: 'match', label: 'Match', render: (c) => (c.cvUnread || c.matchScore === null
      /* No score is made without the CV (6 Oct 2026). */
      ? <span title={c.cvUnreadReason || CV_UNREAD_LABEL}><StatusPill tone="warning">{CV_UNREAD_LABEL}</StatusPill></span>
      : <span className="num" style={{ fontWeight: 700, fontSize: 'var(--fs-body-lg)', color: matchColor(c.matchScore) }}>{c.matchScore}%</span>
    ) },
    { key: 'recs', label: 'Other roles', render: (c) => (c.recommendations.length > 0 ? (
      <div style={{ display: 'grid', gap: 2, fontSize: 'var(--fs-body-sm)' }}>
        {c.recommendations.slice(0, 2).map((rec, ri) => (
          <div key={ri} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span className="num" style={{ fontWeight: 600, color: 'var(--text-1)' }}>{rec.matchScore}%</span>
            <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>{rec.jobTitle}</span>
          </div>
        ))}
        {c.recommendations.length > 2 && (
          <button type="button" className="eq-link-btn" style={{ fontSize: 'var(--fs-caption)' }} onClick={() => setSelectedCandidate(c)}>
            +{c.recommendations.length - 2} more
          </button>
        )}
      </div>
    ) : (
      <Button size="sm" variant="secondary" onClick={() => handleGenerateRecs(c)} loading={generatingFor === c._id}
        icon={<Icon name="sparkle" />}>
        {generatingFor === c._id ? 'Generating…' : 'Generate'}
      </Button>
    )) },
    { key: 'potential', label: 'Potential', render: (c) => (
      <PotentialDropdown resumeId={c._id} value={c.candidatePotential} onChange={handlePotentialChange} name={c.candidateName} />
    ) },
    { key: 'actions', label: '', render: (c) => (
      <div style={{ display: 'flex', gap: 4 }}>
        <Button size="sm" variant="ghost" icon={<Icon name="eye" />} onClick={() => setSelectedCandidate(c)}
          aria-label={`View evidence for ${c.candidateName}`} title="View evidence" />
        {c.recommendations.length > 0 && (
          <Button size="sm" variant="ghost" icon={<Icon name="sparkle" spin={generatingFor === c._id} />} onClick={() => handleGenerateRecs(c)}
            disabled={generatingFor === c._id} aria-label={`Refresh role matches for ${c.candidateName}`} title="Refresh AI recommendations" />
        )}
      </div>
    ) },
  ];

  // On phones the Table stacks: the candidate, then the facts, then View.
  const byKey = Object.fromEntries(columns.map((c) => [c.key, c]));
  const phoneColumns = [byKey.candidate, byKey.status, byKey.match, byKey.potential, byKey.actions];

  return (
    <>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: mobile ? 'var(--fs-display-md)' : 'var(--fs-display-lg)' }}>Talent pool</h1>
          <p style={{ color: 'var(--text-2)', marginTop: 4 }}>
            <span className="num">{total}</span> candidate{total !== 1 ? 's' : ''} across all JDs
          </p>
        </div>
        <Button variant="secondary" onClick={() => load()} disabled={loading} icon={<Icon name="arrow-clockwise" spin={loading} />}>
          Reload
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${mobile ? 140 : 170}px, 1fr))`, gap: 16, alignItems: 'end' }}>
          <div style={{ gridColumn: mobile ? '1 / -1' : 'span 2', minWidth: 0 }}>
            <Input
              id="talent-search"
              label="Search"
              placeholder="Name or email"
              value={search}
              icon={<Icon name="magnifying-glass" style={{ color: 'var(--text-muted)' }} />}
              onChange={e => { setSearch(e.target.value); load({ search: e.target.value }); }}
            />
          </div>
          <Select
            label="JD"
            value={jdFilter}
            options={[{ value: 'all', label: 'All JDs' }, ...jdList.map(j => ({ value: String(j._id), label: j.title }))]}
            onChange={v => {
              setJdFilter(v);
              load({ jdFilter: v });
            }}
          />
          <Select
            label="Stage"
            value={stageFilter}
            options={WORKFLOW_STAGES}
            onChange={v => { setStageFilter(v); load({ stageFilter: v }); }}
          />
          <Select
            label="Min match"
            value={minScore}
            options={MIN_MATCH_OPTIONS}
            onChange={v => { setMinScore(v); load({ minScore: v }); }}
          />
          <Select
            label="Potential"
            value={potentialFilter}
            options={[{ value: 'all', label: 'All' }, ...POTENTIAL_OPTIONS]}
            onChange={v => {
              setPotentialFilter(v);
              load({ potentialFilter: v });
            }}
          />
          {/* Payment filter — the ₹500 application fee */}
          <Select
            label="Fee"
            value={paymentFilter}
            options={FEE_OPTIONS}
            onChange={v => {
              setPaymentFilter(v);
              load({ paymentFilter: v });
            }}
          />
        </div>
      </Card>

      {/* Table */}
      <Card padding="0">
        {loading ? (
          <EmptyState icon={<Icon name="spinner" size={36} spin />} title="Loading candidates" />
        ) : candidates.length === 0 ? (
          <EmptyState icon={<Icon name="users" size={36} />} title="No candidates found" description="Try changing the filters." />
        ) : (
          <Table stacked={mobile} columns={mobile ? phoneColumns : columns} rows={candidates} getRowId={(c) => c._id} />
        )}
        {!loading && candidates.length > 0 && candidates.length < total && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: 16, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--text-2)' }}>
              Showing <span className="num">{candidates.length}</span> of <span className="num">{total}</span>
            </span>
            <Button variant="secondary" onClick={() => load({}, { append: true, pageNo: page + 1 })}>Show more</Button>
          </div>
        )}
      </Card>

      {/* Candidate record */}
      {selectedCandidate && (
        <CandidateEvidencePanel
          candidate={selectedCandidate}
          onClose={() => setSelectedCandidate(null)}
        />
      )}
    </>
  );
}
